import type { ReactNode } from 'react';

import type { ResizeHandle } from './upload-shared/redactionRect';

export type MaskedDocumentUploadTexts = {
  chooseFile: string;
  dropFile: string;
  confirmButton: string;
  rejectButton: string;
  closeButton: string;
  introPopupTitle: string;
  introSensitiveDataHeading: string;
  skipEditingButton: string;
  startEditingButton: string;
  maskingDialogTitle: string;
  reviewDialogTitle: string;
  maskingEditorHint: string;
  closeButtonLabel: string;
  previousImageLabel: string;
  nextImageLabel: string;
  zoomInLabel: string;
  zoomOutLabel: string;
  imageLoading: string;
  overlayAriaLabel: string;
  maskingInstructionsText: string;
  maskingInstructionsWatermarkText: string;
  thumbnailLabel: (params: { index: number; confirmed: boolean }) => string;
  redactionLabel: (params: { index: number }) => string;
  redactionSelectedLabel: (params: { index: number }) => string;
  removeRedactionLabel: (params: { index: number }) => string;
  resizeHandleLabel: (params: { corner: ResizeHandle }) => string;
  editImageAlt: (params: { fileName: string }) => string;
  renderDefaultMaskingInstructions?: (params: {
    watermarkPurpose?: string;
    instructionsText: string;
    instructionsWatermarkText: string;
  }) => ReactNode;
};

export const defaultMaskedDocumentUploadTexts: MaskedDocumentUploadTexts = {
  chooseFile: 'Kies bestand...',
  dropFile: 'Laat bestand los om te uploaden...',
  confirmButton: 'Opslaan en doorgaan',
  rejectButton: 'Verwijder afbeelding',
  closeButton: 'Sluiten',
  introPopupTitle: "Foto's toegevoegd",
  introSensitiveDataHeading: 'Bevat uw kopie gevoelige gegevens?',
  skipEditingButton: 'Verder zonder controle',
  startEditingButton: 'Afbeeldingen bewerken',
  maskingDialogTitle: 'Gegevens afschermen',
  reviewDialogTitle: 'Controleer uw kopie',
  maskingEditorHint: 'Markeer gevoelige delen door erop te tikken en een vlak te slepen. Bij opslaan wordt deze definitief afgedekt met een zwarte balk.',
  closeButtonLabel: 'Sluiten',
  previousImageLabel: 'Vorige afbeelding',
  nextImageLabel: 'Volgende afbeelding',
  zoomInLabel: 'Inzoomen',
  zoomOutLabel: 'Uitzoomen',
  imageLoading: 'Afbeelding laden...',
  overlayAriaLabel: 'Tik op de afbeelding om een zwarte balk toe te voegen',
  maskingInstructionsText:
    'Maak uw burgerservicenummer (BSN) onleesbaar, ook in de cijferreeks onderaan.',
  maskingInstructionsWatermarkText:
    'De tekst "kopie", "{purpose}" en de datum worden automatisch op uw afbeelding geplaatst.',
  thumbnailLabel: ({
index,
confirmed
}) => (
    `Afbeelding ${index + 1}${confirmed ? ', bevestigd' : ', nog te controleren'}`
  ),
  redactionLabel: ({ index }) => `Afscherming ${index + 1}`,
  redactionSelectedLabel: ({ index }) => (
    `Afscherming ${index + 1}, geselecteerd. Pijltjestoetsen verplaatsen, Shift+pijltjestoetsen vergroten of verkleinen vanaf de dichtstbijzijnde rand.`
  ),
  removeRedactionLabel: ({ index }) => `Verwijder afscherming ${index + 1}`,
  resizeHandleLabel: ({ corner }) => {
    const cornerLabels: Record<ResizeHandle, string> = {
      nw: 'linksboven',
      ne: 'rechtsboven',
      sw: 'linksonder',
      se: 'rechtsonder',
    };

    return `Formaat aanpassen ${cornerLabels[corner]}. Gebruik pijltjestoetsen.`;
  },
  editImageAlt: ({ fileName }) => `Bewerk ${fileName}`,
};

export type RedactionEditorTexts = Pick<
  MaskedDocumentUploadTexts,
  | 'imageLoading'
  | 'overlayAriaLabel'
  | 'redactionLabel'
  | 'redactionSelectedLabel'
  | 'removeRedactionLabel'
  | 'resizeHandleLabel'
  | 'editImageAlt'
>;

export function resolveMaskedDocumentUploadTexts(
  texts?: Partial<MaskedDocumentUploadTexts>,
): MaskedDocumentUploadTexts {
  if (!texts) {
    return defaultMaskedDocumentUploadTexts;
  }

  return {
    ...defaultMaskedDocumentUploadTexts,
    ...texts,
  };
}
